/* eslint-disable max-len */
/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable @typescript-eslint/no-use-before-define */
import useCommon from '@utils/hooks/common';
import React, {use, useContext, useEffect, useRef, useState} from 'react';
import styles from './styles';
import {PositionContext, EmojiContext} from '@utils/context';
import {Stage, Container} from '@pixi/react';
import Sticker from '@components/SpriteAnimation/Sticker';
import getConfig from 'next/config';
import * as PIXI from 'pixi.js';
import '@pixi/unsafe-eval';
import JetFighter from '@components/SpriteAnimation/JetFighter';
import {EmojiStatus, PlayerComponent as PlayerComponentInterface} from '@interface';
import WaterSplash from '@components/SpriteAnimation/WaterSplash';
import Bomb from '@components/SpriteAnimation/Bomb';
import Poop from '@components/SpriteAnimation/Poop';
import Smoke from '@components/SpriteAnimation/Smoke';
import getImage from './helper';
import {env} from 'next-runtime-env';
import {get} from 'lodash-es';

interface CanvasAreaProps {
  players: PlayerComponentInterface[];
  stickersDataList: any[];
  stickerQueue: EmojiStatus[];
  handleStickerComplete: (timestamp: any) => void;
  isCanvasMuted: boolean;
}

const CanvasArea = React.memo((props: CanvasAreaProps) => {
  const {classes, t, socket, user} = useCommon({
    withStyles: styles,
    withTranslation: ['table_area', 'best_combination', 'common'],
    observers: ['game', 'socket', 'user'],
  });

  const NEXT_PUBLIC_URL = env('NEXT_PUBLIC_URL');

  const {players, stickersDataList, stickerQueue, handleStickerComplete, isCanvasMuted} = props;
  const {componentsPositions, playerIconCenterPositions, scaleUpValue} =
    useContext(PositionContext);
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  const [canvasPosition, setCanvasPosition] = useState({top: 0, bottom: 0, left: 0, right: 0});
  const [nonAnimatedStickersTextures, setNonAnimatedStickersTextures] = useState<PIXI.Texture[][]>(
    []
  );
  const [animatedStickersTextures, setAnimatedStickersTextures] = useState<PIXI.Texture[][]>([]);
  const [fullScreenAnimationTextures, setFullScreenAnimationTextures] = useState<PIXI.Texture[][]>(
    []
  );
  useState<boolean>(false);

  useEffect(() => {
    setTimeout(() => {
      if (canvasContainerRef.current) {
        const {top, bottom, left, right} = canvasContainerRef.current.getBoundingClientRect();
        setCanvasPosition({top, bottom, left, right});
      }
      // 1000 to make sure the canvas is fully scaled up/down
    }, 1000);
    //! otherwise doesn't work on mobile
  }, [scaleUpValue, playerIconCenterPositions]);

  const animatedStickersAnimations = {
    jetFighter: '/animations/emoji-effects/jet-fighter.json',
    explosion: 'https://pixijs.io/examples/examples/assets/spritesheet/mc.json',
    waterBucket: '/animations/emoji-effects/water-bucket.json',
    waterSplash: '/animations/emoji-effects/water-splash.json',
    bomb: '/animations/emoji-effects/bomb-v2.json',
    explosionV2: '/animations/emoji-effects/explosion-v2.json',
    poop: '/animations/emoji-effects/poop.json',
    poopSlash: '/animations/emoji-effects/poop-splash.json',
    smoke: '/animations/emoji-effects/smokeAnimation.json',
  };

  const fullScreenAnimations = {
    fullScreenSmoke: '/animations/emoji-effects/fullScreenSmoke.json',
    waterSplash: '/animations/emoji-effects/water_splash_fullscreen.json',
    poopSlash: '/animations/emoji-effects/poop_splash_fullscreen.json',
    explosionV2: '/animations/emoji-effects/explosion-v2.json',
  };

  // async: tell js that function will return a promise
  // await: wait for the promise to resolve before continuing execution
  // Promise.all(): takes an array of promises as input & returns a single promise that resolves when all of the input promises have resolved
  // In this case, it's used to wait for all of the textures to be loaded before continuing execution
  // Object.keys(): returns an array of the keys in an object

  const loadAnimatedStickersTextures = async () => {
    const arrays = await Promise.all(
      Object.keys(animatedStickersAnimations).map(async name => {
        const texture = await PIXI.Assets.load(animatedStickersAnimations[name]);
        const frames = Object.keys(texture.data.frames);
        return frames.map(frame => PIXI.Texture.from(frame));
      })
    );
    setAnimatedStickersTextures(arrays);
  };

  const loadFullScreenAnimationsTextures = async () => {
    const arrays = await Promise.all(
      Object.keys(fullScreenAnimations).map(async name => {
        const texture = await PIXI.Assets.load(fullScreenAnimations[name]);
        const frames = Object.keys(texture.data.frames);
        return frames.map(frame => PIXI.Texture.from(frame));
      })
    );
    setFullScreenAnimationTextures(arrays);
  };

  const loadNonAnimatedStickersTextures = async () => {
    const imagesList = stickersDataList?.map(item => `${NEXT_PUBLIC_URL}${item?.image}`);
    const texturePromises = imagesList.map(url => PIXI.Texture.fromURL(url));
    const allTextures = await Promise.all(texturePromises);
    const textureArrays = allTextures.map(texture => [texture]);
    setNonAnimatedStickersTextures(textureArrays);
  };

  useEffect(() => {
    if (typeof stickersDataList !== 'undefined') {
      console.log(' ^^^^ load textures ^^^^ ');
      loadNonAnimatedStickersTextures();
      loadAnimatedStickersTextures();
      loadFullScreenAnimationsTextures();
    }
  }, [stickersDataList]);

  const getMyPlayerId = () => get(user, 'playerInfo.id', null);

  const getPlayerUiSequence = () => {
    const myPlayerIndexInArray = players.findIndex(
      player => player && player?.id === getMyPlayerId()
    );
    if (myPlayerIndexInArray < 0) {
      // Observer mode
      // Place first empty seat closest to the user
      const emptySeatIndex = players.findIndex(player => !player?.id);
      if (emptySeatIndex <= 0) {
        return players;
      }

      const copy = players.map(player => player);
      // Place empty seat at the beginning of array
      const allPlayersBeforeMe = copy.splice(0, emptySeatIndex);
      copy.push(...allPlayersBeforeMe);
      return copy;
    }

    const copy = players.map(player => player);
    const allPlayersBeforeMe = copy.splice(0, myPlayerIndexInArray);
    copy.push(...allPlayersBeforeMe);
    return copy;
  };

  const playerSequence = getPlayerUiSequence();

  const getPlayerPositionIndexForOrder = (playerId: number): number => {
    return playerSequence?.findIndex(sequencedPlayer => sequencedPlayer?.id === playerId);
  };

  interface StickerData {
    animatedSticker: React.MemoExoticComponent<(props) => JSX.Element>;
    stickerAnimations: PIXI.Texture[][];
    fullScreenAnimation: PIXI.Texture[];
  }

  const animatedStickerDataMap: Map<string, StickerData> = new Map([
    [
      getImage('jetFighter'),
      {
        animatedSticker: JetFighter,
        stickerAnimations: [animatedStickersTextures[0], animatedStickersTextures[1]],
        fullScreenAnimation: fullScreenAnimationTextures[0],
      },
    ],
    [
      getImage('waterBucket'),
      {
        animatedSticker: WaterSplash,
        stickerAnimations: [animatedStickersTextures[2], animatedStickersTextures[3]],
        fullScreenAnimation: fullScreenAnimationTextures[1],
      },
    ],
    [
      getImage('bomb'),
      {
        animatedSticker: Bomb,
        stickerAnimations: [animatedStickersTextures[4], animatedStickersTextures[5]],
        fullScreenAnimation: fullScreenAnimationTextures[3],
      },
    ],
    [
      getImage('poop'),
      {
        animatedSticker: Poop,
        stickerAnimations: [animatedStickersTextures[6], animatedStickersTextures[7]],
        fullScreenAnimation: fullScreenAnimationTextures[2],
      },
    ],
    [
      getImage('smoke'),
      {
        animatedSticker: Smoke,
        stickerAnimations: [animatedStickersTextures[8]],
        fullScreenAnimation: fullScreenAnimationTextures[0],
      },
    ],
  ]);

  // times 1.5 coz animation area can be bigger than player's size
  const canvasHeight =
    (componentsPositions?.tableArea?.bottom - componentsPositions?.tableArea?.top) * 1.5 || 0;
  const canvasWidth =
    (componentsPositions?.tableArea?.right - componentsPositions?.tableArea?.left) * 1.5 || 0;

  const stickerProps = {
    scaleUpValue,
    stageWidth: canvasWidth,
    stageHeight: canvasHeight,
    playerIconCenterPositions,
    canvasPosition,
  };

  const findTextureArrayById = (url: string): PIXI.Texture[] | undefined => {
    const matchingArray = nonAnimatedStickersTextures.find(textureArray => {
      const texture = textureArray[0];
      return texture.textureCacheIds.includes(url);
    });
    return matchingArray;
  };

  return (
    <div style={{position: 'relative'}} ref={canvasContainerRef}>
      <Stage
        width={canvasWidth}
        height={canvasHeight}
        options={{
          backgroundAlpha: 0,
          backgroundColor: 'grey',
        }}
      >
        <Container>
          {stickersDataList &&
            stickerQueue.map((sticker, index) => {
              const stickerImage = sticker.image;
              const imageIndex = stickersDataList.findIndex(item => item.image === stickerImage);
              const AnimatedSticker = animatedStickerDataMap.get(stickerImage)?.animatedSticker;
              const stickerAnimations = animatedStickerDataMap.get(stickerImage)?.stickerAnimations;
              const fullScreenAnimation =
                animatedStickerDataMap.get(stickerImage)?.fullScreenAnimation;

              if (!AnimatedSticker)
                return (
                  <Sticker
                    {...stickerProps}
                    key={sticker.timestamp}
                    animation={findTextureArrayById(
                      `${NEXT_PUBLIC_URL}${stickersDataList[imageIndex]?.image}`
                    )}
                    onAnimationComplete={() => handleStickerComplete(sticker.timestamp)}
                    senderPosition={getPlayerPositionIndexForOrder(sticker.playerId)}
                    receiverPosition={getPlayerPositionIndexForOrder(sticker.targetPlayerId)}
                  />
                );
              return (
                <AnimatedSticker
                  {...stickerProps}
                  onAnimationComplete={() => handleStickerComplete(sticker.timestamp)}
                  firstAnimation={stickerAnimations[0]}
                  secondAnimation={stickerAnimations[1]}
                  fullScreenAnimation={fullScreenAnimation}
                  key={sticker.timestamp}
                  timestamp={sticker.timestamp}
                  senderPosition={getPlayerPositionIndexForOrder(sticker.playerId)}
                  receiverPosition={getPlayerPositionIndexForOrder(sticker.targetPlayerId)}
                  isMuted={isCanvasMuted}
                />
              );
            })}
        </Container>
      </Stage>
    </div>
  );
});

CanvasArea.displayName = 'CanvasArea';

export default CanvasArea;
